/*  CreatePortfolioLanding.jsx
    Bootstrap 5.3  +  Font Awesome 4.7   (icon names & syntax adjusted)
    ——————————————————————————————————————————————————————————————— */

import React from 'react';
import { router, usePage } from '@inertiajs/react';

/*  Make sure your base Blade/layout already contains exactly one:
    <link rel="stylesheet"
          href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/4.7.0/css/font-awesome.min.css">
*/

const CreatePortfolioLanding = () => {
  const { entryContext, scriptName, scriptHash } = usePage().props;

  /*  All icon names exist in FA 4.7  */
  const benefits = [
    {
      icon: 'fa-link',
      title: 'Professional URL to share',
      description: 'Get scriptreader.ai/portfolios/yourname to include in pitches and signatures',
    },
    {
      icon: 'fa-file-text-o',
      title: 'Showcase your best work',
      description: 'Highlight your top scripts with analysis scores and loglines',
    },
    {
      icon: 'fa-users',
      title: 'Connect with other writers',
      description: 'Join a community of screenwriters and industry professionals',
    },
    {
      icon: 'fa-eye',
      title: 'Control your privacy',
      description: 'Only people you share your link with can see your work',
    },
  ];

  const handleGetStarted = () => {
    router.visit(
      scriptHash ? `/writer-profile/setup?script=${scriptHash}` : '/writer-profile/setup',
    );
  };

  return (
    /* Tighter vertical spacing on sm/md; full-height centring only on ≥lg */
    <section className="pt-4 pb-6 pb-lg-8 d-lg-flex align-items-lg-center justify-content-lg-center">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-8">
            {/* ──────────────────────────  CARD  ─────────────────────────── */}
            <div className="card shadow-lg border-0">
              {/* Header with custom gradient (no bg-gradient utility) */}
              <div
                className="card-header text-white text-center py-5 border-0"
                style={{
                  backgroundImage: 'linear-gradient(135deg,#4F46E5 0%,#7C3AED 100%)',
                  minHeight: '220px',
                }}
              >
                <i className="fa fa-film fa-2x mb-3" />
                <h1 className="display-5 fw-bold mb-3">
                  {entryContext === 'script'
                    ? `Ready to showcase “${scriptName}”?`
                    : 'Create your writer portfolio'}
                </h1>
                <p className="lead opacity-90 mb-0">
                  Get a professional page to share your scripts&nbsp;– takes&nbsp;10&nbsp;minutes
                </p>
              </div>

              {/* ────────────────────────  CARD BODY  ─────────────────────── */}
              <div className="card-body p-5">
                {/* Context-specific intro */}
                {entryContext === 'script' ? (
                  <div
                    className="alert alert-info border-0 mb-4"
                    style={{ backgroundColor: '#EBF8FF', borderColor: '#BEE3F8' }}
                  >
                    <p className="text-info mb-0 text-center">
                      <strong>“{scriptName}”</strong> and your other work deserve a professional
                      showcase. Create your portfolio to start sharing your scripts with confidence.
                    </p>
                  </div>
                ) : (
                  <div className="text-center mb-5">
                    <p className="lead text-muted">
                      A simple way to showcase your work and share with industry contacts
                    </p>
                  </div>
                )}

                {/* Benefits grid */}
                <div className="row g-4 mb-5">
                  {benefits.map(({ icon, title, description }, i) => (
                    <div key={i} className="col-md-6">
                      <div
                        className="d-flex align-items-start p-3 rounded"
                        style={{ transition: 'background-color .2s' }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.backgroundColor = '#f8f9fa')
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.backgroundColor = 'transparent')
                        }
                      >
                        <i className={`fa ${icon} fa-lg text-primary me-3`} />
                        <div>
                          <h5 className="fw-semibold text-dark mb-2">{title}</h5>
                          <p className="text-muted small mb-0">{description}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Quick facts pill bar */}
                <div
                  className="alert alert-success border-0 mb-5"
                  style={{ backgroundColor: '#F0FDF4', borderColor: '#BBF7D0' }}
                >
                  <div className="row text-center g-3">
                    <div className="col-md-4 d-flex align-items-center justify-content-center">
                      <span className="me-2">⏱️</span>
                      <span className="fw-semibold text-success">10 minutes</span>
                    </div>
                    <div className="col-md-4 d-flex align-items-center justify-content-center">
                      <span className="me-2">✅</span>
                      <span className="fw-semibold text-success">100% Free</span>
                    </div>
                  </div>
                </div>

                {/* What you’ll need list */}
                <div className="bg-light rounded p-4 mb-5">
                  <h5 className="fw-semibold text-dark mb-3">What you'll need:</h5>
                  <ul className="list-unstyled mb-0">
                    <li className="d-flex align-items-center mb-2">
                      <span className="text-success me-2">✅</span>
                      <span className="text-muted">A username for your portfolio URL</span>
                    </li>
                    <li className="d-flex align-items-center mb-2">
                      <span className="text-success me-2">✅</span>
                      <span className="text-muted">A short bio about yourself as a writer</span>
                    </li>
                    <li className="d-flex align-items-center">
                      <span className="text-muted me-2">●</span>
                      <span className="text-muted">Scripts to showcase (optional – add later)</span>
                    </li>
                  </ul>
                </div>

                {/* Primary + secondary CTA */}
                <div className="d-grid gap-3">
                  <button
                    onClick={handleGetStarted}
                    className="btn btn-lg fw-semibold text-white border-0 py-3"
                    style={{ backgroundImage: 'linear-gradient(135deg,#4F46E5 0%,#7C3AED 100%)' }}
                  >
                    Get Started&nbsp;
                    <i className="fa fa-long-arrow-right ms-1" />
                  </button>

                  <a
                    href="/portfolios/daniel-doble"
                    target="_blank"
                    rel="noopener"
                    className="text-primary text-center fw-medium text-decoration-underline"
                  >
                    See an example portfolio first
                  </a>
                </div>

                {/* Fine print */}
                <p className="small text-muted text-center mt-4 mb-0">
                  Your portfolio will only be visible to people you share the link with
                </p>
              </div>
            </div>

            {/* Extra note for script entry flow */}
            {entryContext === 'script' && (
              <p className="small text-muted text-center mt-4">
                Creating a portfolio will let you add “{scriptName}” and showcase it professionally.
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CreatePortfolioLanding;
